import { useCallback } from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import { toast } from 'sonner'
import type { LEDPanel, LEDConfig, WorkspaceElement, PanelConnection } from '@/types/synoptique'

interface ElementGroup {
  name: string
  elementIds: string[]
  permanent?: boolean
}

interface UseAutoGeneratePanelsParams {
  panels: LEDPanel[]
  panelConnections: PanelConnection[]
  workspaceElements: WorkspaceElement[]
  setWorkspaceElements: (fn: (prev: WorkspaceElement[]) => WorkspaceElement[]) => void
  setElementGroups: (fn: (prev: Record<string, ElementGroup>) => Record<string, ElementGroup>) => void
}

export function useAutoGeneratePanels({
  panels,
  panelConnections,
  workspaceElements,
  setWorkspaceElements,
  setElementGroups,
}: UseAutoGeneratePanelsParams) {
  const { t } = useLanguage()

  // Calculer le nombre de pixels par port
  const calculatePortPixels = useCallback((processorId: string, port: number) => {
    const connections = panelConnections.filter(
      conn => conn.processorId === processorId && conn.port === port
    )

    let totalPixels = 0
    connections.forEach(conn => {
      const panel = workspaceElements.find(el => el.id === conn.panelId && el.type === 'panel')
      if (panel && panel.data?.resolution) {
        const resolution = panel.data.resolution
        if (typeof resolution === 'object' && 'width' in resolution && 'height' in resolution) {
          totalPixels += resolution.width * resolution.height
        }
      }
    })

    return { panelCount: connections.length, totalPixels }
  }, [panelConnections, workspaceElements])

  // Obtenir la capacité maximale d'un port (exemple: 650K pixels par port pour la plupart des processeurs)
  const getPortMaxPixels = useCallback((processorId: string) => {
    const processor = workspaceElements.find(el => el.id === processorId && el.type === 'processor')
    if (processor && processor.data?.maxPixelsPerPort) {
      const maxPixels = processor.data.maxPixelsPerPort
      if (typeof maxPixels === 'number') {
        return maxPixels
      } else if (typeof maxPixels === 'string') {
        const parsed = parseInt(maxPixels.replace(/[^0-9]/g, ''))
        return isNaN(parsed) ? 650000 : (maxPixels.includes('K') || maxPixels.includes('k') ? parsed * 1000 : parsed)
      }
    }
    return 650000
  }, [workspaceElements])

  // Générer automatiquement les panneaux basés sur la config LED importée
  const generatePanelsFromLEDConfig = useCallback((
    ledConfig: LEDConfig | null,
    panelType: LEDPanel | null
  ) => {
    if (!ledConfig || !panelType) return []

    const generatedPanels: WorkspaceElement[] = []
    const { columns, rows, panelWidth, panelHeight } = ledConfig.ledWallMode

    const BASE_PANEL_SIZE = 40
    const panelAspect = panelWidth / panelHeight
    const workspacePanelWidth = panelAspect >= 1 ? BASE_PANEL_SIZE : BASE_PANEL_SIZE * panelAspect
    const workspacePanelHeight = panelAspect >= 1 ? BASE_PANEL_SIZE / panelAspect : BASE_PANEL_SIZE

    const totalWidth = columns * workspacePanelWidth
    const totalHeight = rows * workspacePanelHeight
    const startX = -totalWidth / 2
    const startY = -totalHeight / 2

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const panel: WorkspaceElement = {
          id: `panel_${row + 1}_${col + 1}_${Date.now()}_${Math.random()}`,
          type: 'panel',
          data: {
            ...panelType,
            name: `${panelType.name} [${row + 1},${col + 1}]`,
            gridPosition: { row: row + 1, col: col + 1 },
          },
          position: {
            x: startX + col * workspacePanelWidth,
            y: startY + row * workspacePanelHeight
          },
          size: {
            width: workspacePanelWidth,
            height: workspacePanelHeight
          }
        }

        generatedPanels.push(panel)
      }
    }

    return generatedPanels
  }, [])

  // Trouver les panneaux qui correspondent aux dimensions de la mire
  const findMatchingPanels = useCallback((ledConfig: LEDConfig | null) => {
    if (!ledConfig) return []

    const { panelWidth, panelHeight } = ledConfig.ledWallMode

    const matchingPanels = panels.filter(panel => {
      if (panel.resolution && typeof panel.resolution === 'object') {
        return (
          panel.resolution.width === panelWidth &&
          panel.resolution.height === panelHeight
        )
      }
      return false
    })

    return matchingPanels
  }, [panels])

  // Handler pour générer les panneaux automatiquement avec l'image de fond intégrée
  const handleAutoGeneratePanelsWithImage = useCallback(async (
    ledConfig: LEDConfig | null,
    mireImage?: string,
    offsetX: number = 0,
    offsetY: number = 0
  ) => {
    if (!ledConfig) return

    const matchingPanels = findMatchingPanels(ledConfig)

    let selectedPanel: LEDPanel

    if (matchingPanels.length === 0) {
      selectedPanel = {
        id: `generic-panel-${Date.now()}`,
        name: `Panneau générique ${ledConfig.ledWallMode.panelWidth}×${ledConfig.ledWallMode.panelHeight}`,
        brand: 'Générique',
        resolution: {
          width: ledConfig.ledWallMode.panelWidth,
          height: ledConfig.ledWallMode.panelHeight
        },
        pixelPitch: 2.5,
        dimensions: {
          width: 500,
          height: 500
        },
        brightness: 0,
        powerConsumption: 0,
        ipRating: ''
      }

      toast.info(t('synoptic.toast.genericPanelUsed'), {
        description: t('synoptic.toast.genericPanelDesc').replace('{width}', String(ledConfig.ledWallMode.panelWidth)).replace('{height}', String(ledConfig.ledWallMode.panelHeight))
      })
    } else {
      selectedPanel = matchingPanels[0]
    }

    const BASE_PANEL_SIZE = 40
    const panelAspect = ledConfig.ledWallMode.panelWidth / ledConfig.ledWallMode.panelHeight
    const workspacePanelWidth = panelAspect >= 1 ? BASE_PANEL_SIZE : BASE_PANEL_SIZE * panelAspect
    const workspacePanelHeight = panelAspect >= 1 ? BASE_PANEL_SIZE / panelAspect : BASE_PANEL_SIZE
    const totalWidth = ledConfig.ledWallMode.columns * workspacePanelWidth
    const totalHeight = ledConfig.ledWallMode.rows * workspacePanelHeight

    const startX = -totalWidth / 2 + offsetX
    const startY = -totalHeight / 2 + offsetY

    const groupId = `mire_group_${Date.now()}_${Math.random()}`

    const generatedPanels: WorkspaceElement[] = []
    const { columns, rows } = ledConfig.ledWallMode

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const imagePortionX = (col / columns) * 100
        const imagePortionY = (row / rows) * 100
        const imagePortionWidth = (1 / columns) * 100
        const imagePortionHeight = (1 / rows) * 100

        const panel: WorkspaceElement = {
          id: `panel_${row + 1}_${col + 1}_${Date.now()}_${Math.random()}`,
          type: 'panel',
          data: {
            ...selectedPanel,
            name: `${selectedPanel.name} [${row + 1},${col + 1}]`,
            gridPosition: { row: row + 1, col: col + 1 },
            opacity: 1,
            mireImage: mireImage || null,
            mireImagePortion: mireImage ? {
              x: imagePortionX,
              y: imagePortionY,
              width: imagePortionWidth,
              height: imagePortionHeight
            } : null
          },
          position: {
            x: startX + col * workspacePanelWidth,
            y: startY + row * workspacePanelHeight
          },
          size: {
            width: workspacePanelWidth,
            height: workspacePanelHeight
          },
          groupId: groupId
        }

        generatedPanels.push(panel)
      }
    }

    const newGroup = {
      name: ledConfig.name || `Mire ${columns}×${rows}`,
      elementIds: generatedPanels.map(p => p.id),
      permanent: true
    }

    setWorkspaceElements(prev => [...prev, ...generatedPanels])

    setElementGroups(prev => ({
      ...prev,
      [groupId]: newGroup
    }))

    toast.success(t("toast.panelsGenerated", { count: generatedPanels.length }), {
      description: `Grille de ${columns}×${rows} prête à être câblée`
    })
  }, [findMatchingPanels, setWorkspaceElements, setElementGroups, t])

  return {
    calculatePortPixels,
    getPortMaxPixels,
    generatePanelsFromLEDConfig,
    findMatchingPanels,
    handleAutoGeneratePanelsWithImage,
  }
}
